import { Play } from "lucide-react";

const TIKTOK_PLAN = [
    {
        series: "Series 01",
        title: "Making the Soap",
        description:
            "Weigh, melt, blend, pour. We film every batch from oil to loaf so you see exactly what your bar was poured from.",
        hook: "Today we pour 96 bars of Turmeric Gold.",
        poster:
            "https://images.unsplash.com/photo-1601049541289-9b1b7bbbfe19?auto=format&fit=crop&w=1100&q=80",
    },
    {
        series: "Series 02",
        title: "Small Business Journey",
        description:
            "From a kitchen table to the North Shore farmer's market. The real, messy, joyful story of building Island Glow.",
        hook: "Day 1 vs. Day 100.",
        poster:
            "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=1100&q=80",
    },
    {
        series: "Series 03",
        title: "Results",
        description:
            "Before/after stories, skin diaries, and real customer glow-ups. Tag us to be featured in next month's drop.",
        hook: "Three weeks of Turmeric Gold.",
        poster:
            "https://images.unsplash.com/photo-1556228841-a3c527ebefe5?auto=format&fit=crop&w=1100&q=80",
    },
];

const INGREDIENTS = [
    {
        name: "Coconut Oil",
        role: "Base & Lather",
        note: "Cold-pressed Hawaiian coconut. The backbone of every bar.",
    },
    {
        name: "Olive Oil",
        role: "Conditioning",
        note: "Slow, silky lather that nourishes long after the rinse.",
    },
    {
        name: "Shea Butter",
        role: "Skin Food",
        note: "Unrefined shea for a creamy, deeply moisturising finish.",
    },
    {
        name: "Castor Oil",
        role: "Bubble",
        note: "A touch of castor gives a luscious, bakery-soft lather.",
    },
    {
        name: "Turmeric",
        role: "Natural Glow",
        note: "Polyphenols to brighten and balance — the island secret.",
    },
];

export default function Craft() {
    return (
        <div className="bg-cream" data-testid="craft-page">
            {/* Hero */}
            <section className="relative bg-cream-warm py-20 md:py-28 overflow-hidden">
                <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <span className="divider-rule text-brown">
                            The Studio · Cold Process
                        </span>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] leading-[0.98] text-forest mt-6">
                            Handmade
                            <br />
                            <span className="italic text-gold">
                                from scratch.
                            </span>
                        </h1>
                        <p className="mt-8 max-w-xl text-brown leading-relaxed">
                            We don't buy pre-made bases. Every Island Glow bar
                            is mixed cold, traced by hand, and cured for four
                            to six weeks before it earns its wrapper.
                        </p>
                    </div>
                    <div className="md:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden rounded-sm">
                            <img
                                src="https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&w=1200&q=80"
                                alt="Soap studio"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Ingredient spotlight */}
            <section
                className="py-20 md:py-28"
                data-testid="craft-ingredients"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <span className="divider-rule text-brown">
                                Our Oils
                            </span>
                            <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-5">
                                The five in every bar.
                            </h2>
                        </div>
                        <p className="max-w-sm text-brown text-sm leading-relaxed">
                            Flagship Turmeric Coconut Glow — our baseline
                            formula. Each ingredient earns its place.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                        {INGREDIENTS.map((ing, i) => (
                            <article
                                key={ing.name}
                                className="p-6 bg-cream-warm rounded-sm border border-forest/5 hover:border-gold transition-colors rise-in"
                                style={{ animationDelay: `${i * 100}ms` }}
                                data-testid={`ingredient-${i}`}
                            >
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                    0{i + 1}
                                </p>
                                <h3 className="font-serif text-2xl text-forest mt-3">
                                    {ing.name}
                                </h3>
                                <p className="text-[0.7rem] tracking-[0.2em] uppercase text-brown mt-1">
                                    {ing.role}
                                </p>
                                <p className="text-sm text-forest/80 mt-4 leading-relaxed">
                                    {ing.note}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* TikTok Content Plan */}
            <section
                className="bg-forest text-cream py-20 md:py-28 relative overflow-hidden"
                data-testid="craft-tiktok"
            >
                <div className="grain absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
                        <div>
                            <span className="divider-rule text-gold">
                                TikTok Journal
                            </span>
                            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.02]">
                                Three series.
                                <br />
                                <span className="italic text-gold">
                                    One open studio.
                                </span>
                            </h2>
                        </div>
                        <a
                            href="#"
                            className="text-[0.7rem] tracking-[0.3em] uppercase text-gold hover:text-cream transition-colors inline-flex items-center gap-2"
                            data-testid="craft-follow-tiktok"
                        >
                            Follow on TikTok →
                        </a>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {TIKTOK_PLAN.map((s, i) => (
                            <article
                                key={s.title}
                                className="relative rounded-sm overflow-hidden bg-forest-deep rise-in"
                                style={{ animationDelay: `${i * 140}ms` }}
                                data-testid={`tiktok-series-${i}`}
                            >
                                <div className="relative aspect-[9/14] overflow-hidden">
                                    <img
                                        src={s.poster}
                                        alt={s.title}
                                        className="w-full h-full object-cover opacity-80 transition-transform duration-700 hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/40 to-transparent" />
                                    <div className="absolute top-4 left-4 text-[0.6rem] tracking-[0.3em] uppercase text-gold bg-forest/70 px-3 py-1.5 rounded-full">
                                        {s.series}
                                    </div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-gold/90 text-forest flex items-center justify-center">
                                            <Play
                                                size={22}
                                                fill="currentColor"
                                                className="translate-x-0.5"
                                            />
                                        </div>
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 p-6">
                                        <h3 className="font-serif text-2xl text-cream">
                                            {s.title}
                                        </h3>
                                        <p className="text-cream/70 text-sm mt-2 leading-relaxed">
                                            {s.description}
                                        </p>
                                        <p className="mt-4 text-[0.65rem] tracking-[0.3em] uppercase text-gold">
                                            Hook · {s.hook}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Labels */}
            <section
                className="py-20 md:py-28 bg-cream-warm"
                data-testid="craft-labels"
            >
                <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
                    <span className="divider-rule text-brown">
                        On Every Wrapper
                    </span>
                    <h2 className="font-serif text-4xl sm:text-5xl text-forest mt-5">
                        Small writing.
                        <br />
                        <span className="italic text-gold">Big promise.</span>
                    </h2>
                    <div className="mt-12 grid sm:grid-cols-3 gap-6">
                        {[
                            "Handcrafted Natural Soap",
                            "For external use only",
                            "Net weight: 4.5 oz",
                        ].map((t) => (
                            <div
                                key={t}
                                className="p-6 bg-cream rounded-sm border border-forest/10"
                            >
                                <p className="font-serif text-forest text-xl italic">
                                    {t}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
